import {
  EnumProductType,
  PurchasedProductGuestsPerTier,
  SurveyResponseResponses,
} from "gql/generated";
import type { AddressSearchResponseValue } from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";

/** Live dates/guests/names from the accommodation step (invoice admin) merged into cart snapshots on save. */
export type InvoiceAccommodationDraft = {
  productId: string;
  startDateISO: string;
  endDateISO: string;
  guestsPerTier: PurchasedProductGuestsPerTier[];
  guestNames: string[];
};

export type CartItem = {
  productName: string;
  productId: string;
  productType: EnumProductType;
  startDateISO: string;
  endDateISO: string;
  total: number;
  guestsPerTier: PurchasedProductGuestsPerTier[];
  quantity: number;
  sessionId?: string;
  sessionIds?: string[];
  merchVariantId?: string;
  merchVariantLabel?: string;
  /**
   * Post-purchase surfaces only: per-tier lines frozen on the order/invoice
   * at write time. When present they are rendered verbatim — no live tier
   * price lookups — so historical documents never change.
   */
  storedTierLines?: StoredTierLine[];
  /** Fee / renewable / merch option selected for this cart line. */
  selectedTierId?: string | null;
  /** Answers to the add-on's custom fields, keyed by field id. */
  customFieldResponses?: CartCustomFieldResponse[];
  /** Per-business add-on: the businesses it's on for, each with its own answers. */
  businessScopes?: CartBusinessScope[];
};

export type CartCustomFieldResponse = {
  fieldId: string;
  response: string[];
};

export type CartBusinessScope = {
  operatorId: string;
  businessName?: string;
  customFieldResponses?: CartCustomFieldResponse[];
};

/** Mirrors backend `SnapshotTierLine` (frozen at checkout/invoice compose). */
export type StoredTierLine = {
  tierId?: string | null;
  name?: { en?: string | null; es?: string | null; fr?: string | null } | null;
  unitPrice?: number | null;
  quantity?: number | null;
  amount?: number | null;
};

export type CustomerAddressValue = AddressSearchResponseValue | null;

export type CustomerContact = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  complete: boolean;
};

export type IncludedBusiness = {
  operatorEntityId: string;
  businessName?: string;
};

export type BusinessFieldValue = {
  operatorEntityId?: string;
  businessName?: string;
  /** Ordered; index 0 is the primary business and mirrors `operatorEntityId`. */
  includedBusinesses?: IncludedBusiness[];
};

export const getBusinessFieldOperatorIds = (
  value: BusinessFieldValue | null | undefined,
): string[] => {
  if (value?.includedBusinesses?.length) {
    return value.includedBusinesses.map(b => b.operatorEntityId);
  }
  return value?.operatorEntityId ? [value.operatorEntityId] : [];
};

export type DiscountItem = { total: number; discountId: string };

export type QuestionnaireResponse = Pick<
  SurveyResponseResponses,
  "response" | "questionData"
>;
